/**
 * Where the fireworks launch from, and where they are meant to go off.
 *
 * This exists because the firework is described in two places that cannot see each
 * other. *Where* a shell is launched is code — `BoardVFX.combo` picks the pad and
 * the lean. *How hard* it is thrown is data — the rockets layer in
 * `combo-fireworks.json`, which the runtime reads and no code here ever looks at.
 * The burst height is the product of both, so neither file can be checked alone,
 * and the studio can re-save the JSON at any time with no idea that a number in it
 * is load-bearing.
 *
 * So the launch geometry and the intended burst window live here, in a module with
 * no dependency on three.js or on any effect file, and both sides import it:
 * `vfx.js` to place the pads, and `tools/verify-effects.mjs` to do the ballistics
 * and fail the build if the rockets stop clearing the tip of the pyramid — or start
 * clearing it by so much that they burst off the top of the screen.
 *
 * ---------------------------------------------------------------------------
 * Why the burst is aimed at the top of the arc
 * ---------------------------------------------------------------------------
 * The rockets are tuned so a rocket dies exactly when it runs out of climb — its
 * lifetime lands on `t = v/g`. That is worth preserving if you retune them, for two
 * reasons. It is what a firework looks like: the shell decelerates to a stop and
 * detonates at the top rather than mid-climb. And height is *stationary* there, so
 * the spread in lifetime barely moves the burst — anywhere else on the arc, the
 * same jitter would smear the detonations up and down the sky.
 */

import { APEX, BASE_VERTS, faceBasis } from './geometry.js';

/** The plane the tetrahedron stands on. Every base vertex shares it. */
export const GROUND_Y = BASE_VERTS[0][1];

/**
 * How far out from the face the launch pad sits.
 *
 * The rockets are the one effect that does not belong to a face — they climb past
 * the board rather than off it — so this only has to be far enough that the trail
 * is drawn in front of the stonework instead of inside it. It does raise the pad,
 * because every face normal leans upward by a third; `LAUNCH_Y` accounts for that.
 */
export const LAUNCH_STANDOFF = 0.55;

/**
 * The height a shell actually leaves from.
 *
 * All three face normals have the same y component by symmetry, so this is one
 * number rather than one per face — but it is read off a real normal rather than
 * hard-coded, so it survives the board being reshaped.
 */
export const LAUNCH_Y = GROUND_Y + faceBasis(0).normal[1] * LAUNCH_STANDOFF;

/** How far apart the launch pads sit across the base, in board units. */
export const PAD_GAP = 0.62;

/**
 * How far off vertical a shell may lean, in radians.
 *
 * The rockets' own cone is 5 degrees, which is nothing, so without this every shell
 * in a salvo goes up the same line and the whole thing reads as one thick rocket.
 * The lean is *fanned* rather than random: shells splay outward from the centre of
 * the formation, so the salvo opens like a hand. `FAN_JITTER` is then what stops a
 * two-shell combo being a perfect mirror of itself.
 *
 * It also costs height — a leaned shell puts less of its speed into climbing — which
 * is why `burstHeightRange` is given the worst-case lean rather than assuming every
 * rocket goes straight up.
 */
export const FAN = 0.22;
export const FAN_JITTER = 0.06;

/** Gap between one shell going up and the next, so a salvo reads as a salvo. */
export const STAGGER_MS = 260;

/**
 * The window the burst has to land in, as a margin above `APEX` in board units.
 *
 * Above the tip, because a shell that goes off level with the pyramid reads as
 * hitting it. Not far above, because the camera fits the pyramid to the band it is
 * given and there is not much sky past that — a shell that keeps climbing detonates
 * off the top of the viewport, which is the failure this window exists to catch.
 */
export const BURST_MARGIN_MIN = 0.05;
export const BURST_MARGIN_MAX = 0.8;

/** Where a shell thrown at `speed` for `lifetime` seconds under `gravity` ends up. */
export const burstHeight = (speed, lifetime, gravity) =>
  LAUNCH_Y + speed * lifetime - 0.5 * gravity * lifetime * lifetime;

/**
 * The lowest and highest a shell can burst, over the rocket's own random spread and
 * the worst case of the fan.
 *
 * The lean only ever takes height away, so the floor gets the full lean and the
 * ceiling gets none: a shell going straight up is the one that goes highest.
 *
 * @param {{a: number, b: number}} speed the rocket's speed range
 * @param {{a: number, b: number}} lifetime its lifetime range
 * @param {number} gravity its gravity strength
 */
export function burstHeightRange(speed, lifetime, gravity) {
  const upright = Math.cos(FAN + FAN_JITTER);
  const corners = [];
  for (const v of [speed.a, speed.b]) {
    for (const t of [lifetime.a, lifetime.b]) {
      corners.push(burstHeight(v * upright, t, gravity), burstHeight(v, t, gravity));
    }
  }
  return { low: Math.min(...corners), high: Math.max(...corners) };
}

/** The margins that range corresponds to, relative to the tip. */
export function burstMarginRange(speed, lifetime, gravity) {
  const { low, high } = burstHeightRange(speed, lifetime, gravity);
  return { low: low - APEX[1], high: high - APEX[1] };
}
